import React, { useEffect, useState, useCallback } from 'react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { collabAPI, contentCalendarAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from './ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Clapperboard, Calendar, FileText, Palette, CheckCircle2, Loader2, ExternalLink, Video, User } from 'lucide-react';
import GuionTableEditor, { legacyGuionToRows } from './GuionTableEditor';
import { ChannelIconsRow } from './ChannelIcons';
import { CollabBrandKit } from './CollabBrandKit';
import { MarkRecordedDialog } from './MarkRecordedDialog';

const fmtDate = (iso) => iso ? new Date(iso + 'T00:00:00').toLocaleDateString('es-ES', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' }) : '—';
const hasGuion = (p) => (p.guion_table || []).some((r) => Object.values(r || {}).some((v) => String(v || '').trim())) || !!(p.guion || '').trim();

const EstadoBadge = ({ p }) => (
  <span className="text-[11px] font-medium px-2 py-0.5 rounded-full" style={{ background: p.estado_bg || '#eee', color: p.estado_fg || '#333' }} data-testid={`estado-${p.id}`}>{p.estado_label}</span>
);

const PostRow = ({ p, onOpen, onMark, marking }) => (
  <div className="flex items-center gap-3 p-3 rounded-lg border bg-card hover:bg-muted/40 transition-colors" data-testid={`collab-post-${p.id}`}>
    <div className="w-14 text-center shrink-0">
      <div className="text-lg font-bold leading-none">{p.fecha ? p.fecha.slice(8, 10) : '—'}</div>
      <div className="text-[10px] uppercase text-muted-foreground">{p.fecha ? new Date(p.fecha + 'T00:00:00').toLocaleDateString('es-ES', { month: 'short' }) : ''}</div>
    </div>
    <button className="flex-1 min-w-0 text-left" onClick={() => onOpen(p)} data-testid={`open-post-${p.id}`}>
      <p className="text-sm font-medium truncate">{p.titulo || 'Sin título'}</p>
      <div className="flex items-center gap-2 mt-0.5 flex-wrap">
        <EstadoBadge p={p} />
        {p.tipo && <span className="text-[11px] text-muted-foreground capitalize">{p.tipo}</span>}
        <ChannelIconsRow post={p} />
        {hasGuion(p) && <span className="text-[11px] text-emerald-700 inline-flex items-center gap-1"><FileText className="w-3 h-3" /> guion</span>}
        {p.quien_graba && <span className="text-[11px] text-muted-foreground inline-flex items-center gap-1"><User className="w-3 h-3" /> graba: {p.quien_graba}</span>}
      </div>
    </button>
    {p.can_mark_recorded && (
      <Button size="sm" onClick={() => onMark(p)} disabled={marking === p.id} className="bg-emerald-600 hover:bg-emerald-700 shrink-0" data-testid={`mark-recorded-${p.id}`}>
        {marking === p.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <><CheckCircle2 className="w-4 h-4 mr-1" /> Grabado</>}
      </Button>
    )}
  </div>
);

export default function CollaboratorPortal() {
  const [clients, setClients] = useState(null);
  const [clientId, setClientId] = useState('');
  const [posts, setPosts] = useState([]);
  const [recordings, setRecordings] = useState([]);
  const [brandKit, setBrandKit] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(null);
  const [marking, setMarking] = useState(null);
  const [toMark, setToMark] = useState(null);

  useEffect(() => {
    collabAPI.myClients().then((c) => { setClients(c); if (c.length) setClientId(c[0].id); else setLoading(false); })
      .catch((e) => { toast.error(e?.message || 'No se pudieron cargar tus clientes'); setClients([]); setLoading(false); });
  }, []);

  const load = useCallback(async () => {
    if (!clientId) return;
    setLoading(true);
    try {
      const [p, r, b] = await Promise.all([collabAPI.content(clientId), collabAPI.recordings(clientId), collabAPI.brandKit(clientId)]);
      setPosts(p); setRecordings(r); setBrandKit(b);
    } catch (e) { toast.error(e?.message || 'Error cargando datos'); }
    setLoading(false);
  }, [clientId]);
  useEffect(() => { load(); }, [load]);

  const mark = (p) => setToMark(p);
  const confirmMark = async (nota) => {
    const p = toMark;
    setMarking(p.id);
    try {
      await collabAPI.markRecorded(p.id, nota);
      toast.success(nota ? 'Marcada como grabada con tu nota. El equipo ya lo sabe.' : 'Marcada como grabada. El equipo de Karma Digital ya lo sabe.');
      setToMark(null); setOpen(null); await load();
    } catch (e) { toast.error(e?.message || 'No se pudo marcar'); }
    setMarking(null);
  };

  const client = (clients || []).find((c) => c.id === clientId);
  const guiones = posts.filter(hasGuion);
  const pendientes = posts.filter((p) => p.can_mark_recorded);

  if (clients && clients.length === 0) {
    return <div className="text-center py-20 text-muted-foreground" data-testid="collab-no-clients">Todavía no tienes ningún cliente asignado. Contacta con Karma Digital.</div>;
  }

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6" data-testid="collab-portal">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2"><Clapperboard className="w-7 h-7 text-primary" /> Contenido a grabar</h1>
          <p className="text-muted-foreground mt-1">Publicaciones pendientes de grabación, guiones, fechas y material de referencia{client ? ` de ${client.nombre}` : ''}.</p>
        </div>
        {clients && clients.length > 1 && (
          <select value={clientId} onChange={(e) => setClientId(e.target.value)} className="border rounded-lg px-3 py-2 text-sm bg-background h-10" data-testid="collab-client-select">
            {clients.map((c) => <option key={c.id} value={c.id}>{c.nombre}</option>)}
          </select>
        )}
      </div>

      <div className="grid grid-cols-3 gap-3">
        <Card><CardContent className="pt-4 pb-3"><p className="text-xs text-muted-foreground">Por grabar ahora</p><p className="text-2xl font-bold text-purple-700" data-testid="stat-pendientes">{pendientes.length}</p></CardContent></Card>
        <Card><CardContent className="pt-4 pb-3"><p className="text-xs text-muted-foreground">Con guion</p><p className="text-2xl font-bold" data-testid="stat-guiones">{guiones.length}</p></CardContent></Card>
        <Card><CardContent className="pt-4 pb-3"><p className="text-xs text-muted-foreground">Fechas de grabación</p><p className="text-2xl font-bold" data-testid="stat-grabaciones">{recordings.length}</p></CardContent></Card>
      </div>

      {loading ? <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div> : (
        <Tabs defaultValue="calendario">
          <TabsList className="grid grid-cols-4 w-full">
            <TabsTrigger value="calendario" data-testid="tab-calendario"><Calendar className="w-4 h-4 mr-1" /> Calendario</TabsTrigger>
            <TabsTrigger value="guiones" data-testid="tab-guiones"><FileText className="w-4 h-4 mr-1" /> Guiones</TabsTrigger>
            <TabsTrigger value="grabaciones" data-testid="tab-grabaciones"><Video className="w-4 h-4 mr-1" /> Fechas de grabación</TabsTrigger>
            <TabsTrigger value="material" data-testid="tab-material"><Palette className="w-4 h-4 mr-1" /> Material</TabsTrigger>
          </TabsList>
          <TabsContent value="calendario" className="space-y-2 mt-4">
            {posts.length === 0 ? <p className="text-sm text-muted-foreground text-center py-10">No hay publicaciones pendientes de grabar.</p>
              : posts.map((p) => <PostRow key={p.id} p={p} onOpen={setOpen} onMark={mark} marking={marking} />)}
          </TabsContent>
          <TabsContent value="guiones" className="space-y-2 mt-4">
            {guiones.length === 0 ? <p className="text-sm text-muted-foreground text-center py-10">Ninguna publicación tiene guion todavía.</p>
              : guiones.map((p) => <PostRow key={p.id} p={p} onOpen={setOpen} onMark={mark} marking={marking} />)}
          </TabsContent>
          <TabsContent value="grabaciones" className="space-y-2 mt-4">
            {recordings.length === 0 ? <p className="text-sm text-muted-foreground text-center py-10">No hay fechas de grabación programadas.</p>
              : recordings.map((r) => (
                <div key={r.id} className="flex items-center gap-3 p-3 rounded-lg border bg-card" data-testid={`recording-${r.id}`}>
                  <Video className="w-5 h-5 text-purple-600 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium">{r.titulo}</p>
                    <p className="text-xs text-muted-foreground capitalize">{fmtDate(r.fecha)}{r.fecha_fin && r.fecha_fin !== r.fecha ? ` → ${fmtDate(r.fecha_fin)}` : ''}{r.hora_inicio ? ` · ${r.hora_inicio}${r.hora_fin ? `–${r.hora_fin}` : ''}` : ''}</p>
                    {r.descripcion && <p className="text-xs text-muted-foreground mt-0.5">{r.descripcion}</p>}
                  </div>
                </div>
              ))}
          </TabsContent>
          <TabsContent value="material" className="mt-4">
            <CollabBrandKit kit={brandKit} />
          </TabsContent>
        </Tabs>
      )}

      <MarkRecordedDialog post={toMark} onOpenChange={(o) => !o && setToMark(null)} onConfirm={confirmMark} saving={!!marking} />

      <Dialog open={!!open} onOpenChange={(o) => !o && setOpen(null)}>
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto grid-cols-[minmax(0,1fr)]" data-testid="collab-post-dialog">
          {open && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 flex-wrap">{open.titulo || 'Sin título'} <EstadoBadge p={open} /></DialogTitle>
                <DialogDescription className="capitalize">{fmtDate(open.fecha)}{open.tipo ? ` · ${open.tipo}` : ''}{open.quien_graba ? ` · Graba: ${open.quien_graba}` : ''}</DialogDescription>
              </DialogHeader>
              {(open.color_portada_file_id || open.portada_file_id) && (
                <div className="flex gap-3 flex-wrap">
                  {open.color_portada_file_id && <div><p className="text-xs text-muted-foreground mb-1">Color / diseño de portada</p><img src={contentCalendarAPI.getAttachmentUrl(open.id, open.color_portada_file_id)} alt="Portada" className="h-40 rounded-lg border object-cover" data-testid="post-color-portada" /></div>}
                  {open.portada_file_id && <div><p className="text-xs text-muted-foreground mb-1">Portada</p><img src={contentCalendarAPI.getAttachmentUrl(open.id, open.portada_file_id)} alt="Portada" className="h-40 rounded-lg border object-cover" data-testid="post-portada" /></div>}
                </div>
              )}
              <div className="min-w-0">
                <p className="text-sm font-semibold mb-2 flex items-center gap-1"><FileText className="w-4 h-4" /> Guion</p>
                {hasGuion(open) ? <div className="min-w-0 max-w-full overflow-x-auto"><GuionTableEditor rows={(open.guion_table && open.guion_table.length) ? open.guion_table : legacyGuionToRows(open.guion)} readOnly /></div>
                  : <p className="text-sm text-muted-foreground italic">Esta publicación aún no tiene guion.</p>}
              </div>
              {open.can_mark_recorded && (
                <div className="flex justify-end pt-2">
                  <Button onClick={() => mark(open)} disabled={marking === open.id} className="bg-emerald-600 hover:bg-emerald-700" data-testid="dialog-mark-recorded-btn"><CheckCircle2 className="w-4 h-4 mr-1" /> Marcar como grabado</Button>
                </div>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </motion.div>
  );
}
